"use client";

import { useState } from "react";
import { SmartCaptcha, captchaEnabled } from "../SmartCaptcha";
import { applyPhoneInput, phoneToE164 } from "@/lib/phoneMask";

type ApplicantType = "with-car" | "driver-only";

/**
 * Форма заявки на участие в форуме (по факту — текущее мероприятие).
 *
 * Параметры:
 *  - source — техническое название источника заявки (для backend log).
 *  - applicationEmail — email получатель (приходит из admin-настроек
 *    `current_event.application_email`). Может быть несколько через запятую.
 *  - telegramChatId — chat_id для дублирования в Telegram (например для
 *    супергрупп формат -100XXXXXXXXXX). Из admin-настроек.
 *  - hashtag — добавляется в Telegram-сообщение и в subject email
 *    (например "#ЗАЯВКА_ПМЭФ2026").
 *
 * Submit на /api/lead. Backend читает поля routing и сам решает, как
 * доставлять (email/telegram). Если applicationEmail и telegramChatId
 * не переданы — используется дефолтная логика (city-based в gateway).
 */
export default function PmefApplicationForm({
  source = "pmef-2026",
  applicationEmail,
  telegramChatId,
  hashtag,
  emailFrom,
}: {
  source?: string;
  applicationEmail?: string;
  telegramChatId?: string;
  hashtag?: string;
  /** Адрес-отправитель уведомлений (`info@autopilot.rent` по умолчанию в backend). */
  emailFrom?: string;
}) {
  const [type, setType] = useState<ApplicantType>("with-car");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  // Поля только для таба «С автомобилем».
  const [carBrand, setCarBrand] = useState("");
  const [carModel, setCarModel] = useState("");
  const [carYear, setCarYear] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError("Заполните имя и телефон");
      return;
    }
    if (type === "with-car" && (!carBrand.trim() || !carModel.trim())) {
      setError("Укажите марку и модель автомобиля");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const composed = [
        `Тип: ${type === "with-car" ? "водитель с автомобилем" : "водитель без авто"}`,
        type === "with-car" && carBrand && `Марка: ${carBrand}`,
        type === "with-car" && carModel && `Модель: ${carModel}`,
        type === "with-car" && carYear && `Год: ${carYear}`,
        comment && `Комментарий: ${comment}`,
      ]
        .filter(Boolean)
        .join("\n");
      // routing — поля, которые backend использует чтобы понять КУДА слать.
      // Если не заданы, backend применит дефолтные правила.
      const routing: Record<string, string> = {};
      if (applicationEmail) routing.application_email = applicationEmail;
      if (telegramChatId) routing.telegram_chat_id = telegramChatId;
      if (hashtag) routing.hashtag = hashtag;
      if (emailFrom) routing.email_from = emailFrom;
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          phone: phoneToE164(phone),
          email,
          comment: composed,
          source,
          smartCaptchaToken: captchaToken,
          ...routing,
        }),
      });
      if (!res.ok) throw new Error("submit failed");
      setDone(true);
    } catch {
      setError("Не удалось отправить. Позвоните +7 495 108-48-48.");
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <div className="overflow-hidden rounded-[20px] bg-white p-8 text-center sm:rounded-[24px] sm:p-12">
        <div className="mx-auto mb-5 inline-flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12l5 5 9-11" />
          </svg>
        </div>
        <h3 className="mb-2 text-[22px] font-medium text-text sm:text-[26px]">Заявка отправлена</h3>
        <p className="text-[14px] text-text-muted sm:text-[15px]">Менеджер свяжется с вами в течение рабочего дня.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="overflow-hidden rounded-[20px] bg-white shadow-[0_4px_30px_-6px_rgba(0,51,161,0.08)] ring-1 ring-black/[0.04] sm:rounded-[24px]"
    >
      {/* Type tabs — uppercase, как в оригинале */}
      <div className="flex">
        <TypeTab active={type === "with-car"} onClick={() => setType("with-car")}>
          ВОДИТЕЛЬ С АВТОМОБИЛЕМ
        </TypeTab>
        <TypeTab active={type === "driver-only"} onClick={() => setType("driver-only")}>
          ВОДИТЕЛЬ
        </TypeTab>
      </div>

      <div className="p-6 sm:p-8 lg:p-10">
        <div className="grid gap-4 sm:gap-5">
          <Field
            label="Ваше имя"
            required
            value={name}
            onChange={setName}
            placeholder="Иван Иванов"
          />
          <Field
            label="Телефон"
            required
            type="tel"
            value={phone}
            onChange={(v) => setPhone(applyPhoneInput(v, phone))}
            placeholder="+7 (___) ___-__-__"
          />
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
          />

          {/* Car fields — только для таба «С автомобилем». На переключении
              в «Водитель» поля скрываются (значения сохраняются в state,
              но не отправляются — фильтрация в composed). */}
          {type === "with-car" && (
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
              <Field
                label="Марка авто"
                required
                value={carBrand}
                onChange={setCarBrand}
                placeholder="Mercedes-Benz"
              />
              <Field
                label="Модель"
                required
                value={carModel}
                onChange={setCarModel}
                placeholder="E-Class"
              />
              <Field
                label="Год выпуска"
                value={carYear}
                onChange={setCarYear}
                placeholder="2023"
                className="sm:col-span-2"
              />
            </div>
          )}

          <Field
            label="Комментарий"
            textarea
            value={comment}
            onChange={setComment}
            placeholder={
              type === "with-car"
                ? "Дополнительная информация: документы, ОСАГО/КАСКО, цвет авто"
                : "Стаж, категории прав, дополнительная информация"
            }
          />
        </div>

        {error && (
          <p className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-[14px] text-red-700">{error}</p>
        )}

        <div className="mt-5">
          <SmartCaptcha onToken={setCaptchaToken} />
        </div>

        <button
          type="submit"
          disabled={sending || (captchaEnabled && !captchaToken)}
          className="mt-7 w-full rounded-[14px] bg-primary py-4 text-[14px] font-semibold uppercase tracking-[1.5px] text-white transition hover:bg-primary-hover disabled:opacity-60 sm:text-[15px]"
        >
          {sending ? "ОТПРАВЛЯЕМ…" : "ОТПРАВИТЬ"}
        </button>
        <p className="mt-3 text-center text-[12px] leading-[1.4] text-text-muted">
          Нажимая «Отправить», вы соглашаетесь на обработку персональных данных.
        </p>
      </div>
    </form>
  );
}

function TypeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 px-3 py-4 text-[11px] font-semibold uppercase tracking-[1px] transition sm:text-[13px] sm:tracking-[1.5px] ${
        active
          ? "bg-white text-text"
          : "bg-primary text-white hover:bg-primary-hover"
      }`}
    >
      {children}
    </button>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  textarea,
  className = "",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  textarea?: boolean;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="text-[12px] font-medium uppercase tracking-[0.5px] text-text-muted">
        {label}
        {required && <span className="text-primary"> *</span>}
      </span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full resize-y rounded-[12px] border border-black/10 bg-bg px-4 py-3 text-[15px] text-text outline-none transition placeholder:text-text-muted/70 focus:border-primary focus:bg-white"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-[12px] border border-black/10 bg-bg px-4 py-3 text-[15px] text-text outline-none transition placeholder:text-text-muted/70 focus:border-primary focus:bg-white"
        />
      )}
    </label>
  );
}
